import React, { Children, useCallback, useRef } from "react";
import type { Ref } from "react";
import type { ViewStyle, LayoutChangeEvent } from "react-native";
import { View, ActivityIndicator, Platform } from "react-native";
import type { SharedValue } from "react-native-reanimated";
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
} from "react-native-reanimated";
import { useAnimatedVariantColor } from "react-native-unistyles/reanimated";

import Icon from "$src/core/components/atoms/Icon";
import Pressable from "$src/core/components/atoms/Pressable";
import Text from "$src/core/components/atoms/Text/Text";
import { TV_PARALLAX } from "$src/core/constants/tv-parallax";
import useAnalytics from "$src/core/hooks/use-analytics";
import type { IconName } from "$src/core/models/icon";
import CustomPlatform from "$src/core/utils/platform/platform";

import $, { useAnimatedStyles } from "./Button.styles";

const AnimatedLoader = Animated.createAnimatedComponent(ActivityIndicator);

/**
 * The spinner and its 4 mappers used to live in every Button, although almost
 * no button is ever loading. Mounted only while `isLoading`, so the other
 * buttons on a TV screen carry 4 mappers less each.
 */
const ButtonLoader = ({
  isFocused,
  dimensions,
  loaderStyle,
  iconStyle,
  iconFocusedStyle,
}: {
  isFocused: SharedValue<boolean>;
  dimensions: SharedValue<{ width: number; height: number } | null>;
  loaderStyle: typeof $.loader;
  iconStyle: typeof $.icon;
  iconFocusedStyle: typeof $.iconFocused;
}) => {
  const dimensionsStyle = useAnimatedStyle(() => ({
    width: dimensions.value?.width,
    height: dimensions.value?.height,
  }));

  const defaultStyle = useAnimatedStyle(() => ({
    display: isFocused.value ? "none" : "flex",
  }));

  const focusedStyle = useAnimatedStyle(() => ({
    display: isFocused.value ? "flex" : "none",
  }));

  return (
    <Animated.View style={[loaderStyle, dimensionsStyle]}>
      <AnimatedLoader
        size={iconStyle.width}
        color={iconStyle.color}
        style={[iconStyle, defaultStyle]}
      />
      <AnimatedLoader
        size={iconStyle.width}
        color={iconFocusedStyle.color}
        style={[iconStyle, focusedStyle]}
      />
    </Animated.View>
  );
};

type Preset = "primary" | "secondary" | "transparent" | "link";

export type ButtonProps = {
  ref?: Ref<View>;
  name: string;
  preset?: Preset;
  size?: "sm" | "md" | "lg";
  align?: "left" | "center" | "right";
  label?: string;
  icon?: IconName;
  isRounded?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isInGroup?: boolean;
  isLastInGroup?: boolean;
  isFirstInGroup?: boolean;
  groupDirection?: "column" | "row";
  onPress: () => void;
  onLongPress?: () => void;
  style?: View["props"]["style"];
  hitSlop?: number;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  focusable?: boolean;
  testID?: string;
};

export const ButtonGroup = ({
  direction = "column",
  style,
  children,
}: {
  direction?: "column" | "row";
  style?: ViewStyle;
  children:
    | React.ReactElement<ButtonProps>
    | (
        | React.ReactElement<ButtonProps>
        | React.ReactElement<ButtonProps>[]
        | undefined
        | boolean
      )[];
}) => {
  $.useVariants({ groupDirection: direction });

  const validChildren = Children.toArray(children).filter(
    React.isValidElement,
  ) as React.ReactElement<ButtonProps>[];

  return (
    <View style={[$.group, style]}>
      {validChildren.map((child, index) =>
        React.cloneElement(child, {
          isInGroup: true,
          isLastInGroup: index === validChildren.length - 1,
          isFirstInGroup: index === 0,
          groupDirection: direction,
        }),
      )}
    </View>
  );
};

const Button = ({
  ref: forwardedRef,
  name,
  label,
  icon,
  isRounded,
  isDisabled,
  isLoading,
  isInGroup,
  isLastInGroup,
  isFirstInGroup,
  groupDirection,
  onPress,
  onLongPress,
  style,
  size = "md",
  align = "center",
  hitSlop,
  accessibilityLabel,
  accessibilityHint,
  focusable,
  testID,
  ...restProps
}: ButtonProps) => {
  const preset = CustomPlatform.select<ButtonProps["preset"]>({
    // TV only uses transparent buttons, which then have a focus state of primary, except for link buttons
    tv: restProps.preset === "link" ? "link" : "transparent",
    default: restProps.preset,
  });

  $.useVariants({
    preset,
    size,
    align,
    isDisabled,
    isRounded,
    isSquared: !label,
    isLoading,
    isInGroup,
    isLastInGroup,
    isFirstInGroup,
    groupDirection,
  });

  const { trackEvent } = useAnalytics();

  const isFocused = useSharedValue(false);
  const dimensions = useSharedValue<{ width: number; height: number } | null>(
    null,
  );

  const { pressableStyle } = useAnimatedStyles({ isFocused });

  const iconColorDefault = useAnimatedVariantColor($.icon, "color");
  const iconColorFocused = useAnimatedVariantColor($.iconFocused, "color");

  const iconColor = useDerivedValue(() =>
    isFocused.value ? iconColorFocused.value : iconColorDefault.value,
  );

  const labelColorDefault = useAnimatedVariantColor($.label, "color");
  const labelColorFocused = useAnimatedVariantColor($.labelFocused, "color");

  const labelStyle = useAnimatedStyle(() => {
    if (Platform.isTV) {
      return {
        color: isFocused.value
          ? labelColorFocused.value
          : labelColorDefault.value,
      };
    }

    return {};
  });

  const ref = useRef<View | null>(null);

  const setRef = useCallback(
    (node: View | null) => {
      ref.current = node;

      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        // eslint-disable-next-line no-param-reassign
        forwardedRef.current = node;
      }
    },
    [forwardedRef],
  );

  const handlePress = useCallback(() => {
    onPress();
    trackEvent("button_interaction", {
      button_name: name,
      button_type: `button_${preset}`,
      button_label: label,
    });
  }, [label, name, onPress, preset, trackEvent]);

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { width, height } = event.nativeEvent.layout;
      dimensions.set({ width, height });
    },
    [dimensions],
  );

  return (
    <Pressable
      ref={setRef}
      style={[$.pressable, Platform.OS !== "web" && pressableStyle, style]}
      disabled={isDisabled}
      onLayout={handleLayout}
      onPress={handlePress}
      onLongPress={onLongPress}
      onFocus={() => isFocused.set(true)}
      onBlur={() => isFocused.set(false)}
      hitSlop={hitSlop}
      tvParallaxProperties={TV_PARALLAX.prominent}
      focusable={focusable}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
    >
      {isLoading ? (
        <ButtonLoader
          isFocused={isFocused}
          dimensions={dimensions}
          loaderStyle={$.loader}
          iconStyle={$.icon}
          iconFocusedStyle={$.iconFocused}
        />
      ) : (
        <>
          {icon &&
            (Platform.isTV ? (
              <Icon.Animated name={icon} style={$.icon} color={iconColor} />
            ) : (
              <Icon name={icon} style={$.icon} />
            ))}

          {label &&
            (Platform.isTV ? (
              <Text.Animated
                accessibilityRole="none"
                preset="span"
                style={[$.label, labelStyle]}
                numberOfLines={1}
              >
                {label}
              </Text.Animated>
            ) : (
              <Text
                accessibilityRole="none"
                preset="span"
                style={$.label}
                numberOfLines={1}
              >
                {label}
              </Text>
            ))}
        </>
      )}
    </Pressable>
  );
};

export default Button;
